import { useMutation as useConnectMutation } from '@connectrpc/connect-query'
import { t } from '@lingui/core/macro'

import { Album_AlbumAction } from '@jotta/grpc-connect-openapi/album'
import { patchAlbum as setAlbumTitle } from '@jotta/grpc-connect-openapi/albumQuery'
import { queryClient } from '@jotta/query'
import type { PhotosApi } from '@jotta/types/PhotosApi'
import { showToast } from '@jotta/ui/ToastProvider'

import {
  getAlbumQuery,
  invalidateAlbumMetadataQueries,
} from '../../../api/useAlbum'

export function useAlbumTitleAction({
  albumIdOrShareId,
  enabled = true,
}: {
  albumIdOrShareId: string
  enabled?: boolean
}) {
  const { data: album } = getAlbumQuery({
    variables: { albumIdOrShareId },
    enabled: enabled && Boolean(albumIdOrShareId),
    select: data => data.album,
  })
  const albumId = album?.id
  const canChangeTitle =
    album?.actions.includes(Album_AlbumAction.CHANGE_TITLE) ?? false
  const { mutateAsync, isPending } = useConnectMutation(setAlbumTitle, {
    throwOnError: false,
    onSuccess: async (_response, { albumId, newAlbumTitle }) => {
      if (!albumId || newAlbumTitle === undefined) {
        return
      }

      queryClient.setQueriesData<PhotosApi.AlbumInfo>(
        { queryKey: ['album', 'info', { albumId }] },
        current => (current ? { ...current, title: newAlbumTitle } : current),
      )

      await invalidateAlbumMetadataQueries(albumId)

      showToast({
        message: t`Album title updated.`,
        hasCloseButton: true,
        duration: 'short',
      })
    },
  })

  async function changeTitle(nextTitle: string) {
    if (!albumId || !canChangeTitle) {
      return
    }

    try {
      await mutateAsync({ albumId, newAlbumTitle: nextTitle })
    } catch (error) {
      showToast({
        message: t`Cannot update title. Please try again later.`,
        hasCloseButton: true,
        duration: 'short',
      })
      throw error
    }
  }

  return { canChangeTitle, changeTitle, isPending }
}
